import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  CheckCircle,
  DollarSign,
  Award,
  Calendar,
  User,
  MapPin,
  FileText,
  Download,
  Share2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface ReportData {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'verified' | 'valuated' | 'certified';
  submittedBy: string;
  submittedAt: string;
  location: string;
  category: string;
  fields: Array<{
    label: string;
    value: string | number;
    type?: 'text' | 'number' | 'date' | 'textarea';
  }>;
  attachments?: Array<{
    name: string;
    url: string;
    size: string;
  }>;
}

// Mock data - replace with API call
const mockReportData: Record<string, ReportData> = {
  '1': {
    id: '1',
    title: 'Organic Farm Certification Request',
    description: 'Request for organic certification for 50 hectares of farmland',
    status: 'pending',
    submittedBy: 'João Silva',
    submittedAt: '2024-01-15T10:30:00Z',
    location: 'São Paulo, Brazil',
    category: 'Organic Farming',
    fields: [
      { label: 'Farm Name', value: 'Fazenda Verde', type: 'text' },
      { label: 'Total Area (hectares)', value: 50, type: 'number' },
      { label: 'Crop Types', value: 'Coffee, Sugarcane, Corn', type: 'text' },
      { label: 'Organic Since', value: '2020-01-01', type: 'date' },
      { label: 'Certification Body', value: 'IBD Certificações', type: 'text' },
      { label: 'Additional Notes', value: 'Farm has been practicing organic agriculture for 4 years. All pesticides and synthetic fertilizers have been eliminated.', type: 'textarea' },
    ],
    attachments: [
      { name: 'farm-layout.pdf', url: '#', size: '2.3 MB' },
      { name: 'soil-analysis.pdf', url: '#', size: '1.8 MB' },
    ],
  },
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  verified: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  valuated: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  certified: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
};

const ReportDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const report = mockReportData[id || '1'];

  if (!report) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Report Not Found</h2>
          <p className="text-muted-foreground mb-4">The report you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/reports')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleVerify = async () => {
    setIsProcessing(true);
    try {
      // TODO: Call API to verify report
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Report verified successfully');
      // Update status or refetch data
    } catch (error) {
      toast.error('Failed to verify report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleValuate = async () => {
    setIsProcessing(true);
    try {
      // TODO: Call API to valuate report
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Report valuated successfully');
    } catch (error) {
      toast.error('Failed to valuate report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCertify = async () => {
    setIsProcessing(true);
    try {
      // TODO: Call API to certify report
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Report certified successfully');
    } catch (error) {
      toast.error('Failed to certify report');
    } finally {
      setIsProcessing(false);
    }
  };

  const canVerify = report.status === 'pending';
  const canValuate = report.status === 'verified';
  const canCertify = report.status === 'valuated';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">{report.title}</h1>
            <Badge className={statusColors[report.status]} variant="secondary">
              {report.status}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">{report.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Report Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>Report Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Submitted By</p>
                    <p className="font-medium">{report.submittedBy}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Submitted At</p>
                    <p className="font-medium">{formatDate(report.submittedAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Location</p>
                    <p className="font-medium">{report.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Category</p>
                    <p className="font-medium">{report.category}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Fields */}
          <Card>
            <CardHeader>
              <CardTitle>Report Details</CardTitle>
              <CardDescription>Submitted form data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {report.fields.map((field, index) => (
                  <div key={index}>
                    <label className="text-sm font-medium text-muted-foreground">
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <p className="mt-1 text-sm whitespace-pre-wrap bg-accent/50 p-3 rounded-md">
                        {field.value}
                      </p>
                    ) : (
                      <p className="mt-1 text-sm font-medium">{field.value}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          {report.attachments && report.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Attachments</CardTitle>
                <CardDescription>Uploaded documents and files</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{attachment.name}</p>
                          <p className="text-xs text-muted-foreground">{attachment.size}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Workflow Actions */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Workflow Actions</CardTitle>
              <CardDescription>Process this report through the verification workflow</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Verify */}
              <Button
                className="w-full justify-start"
                variant={canVerify ? 'default' : 'outline'}
                disabled={!canVerify || isProcessing}
                onClick={handleVerify}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Verify Report
              </Button>

              {/* Valuate */}
              <Button
                className="w-full justify-start"
                variant={canValuate ? 'default' : 'outline'}
                disabled={!canValuate || isProcessing}
                onClick={handleValuate}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Valuate Report
              </Button>

              {/* Certify */}
              <Button
                className="w-full justify-start"
                variant={canCertify ? 'default' : 'outline'}
                disabled={!canCertify || isProcessing}
                onClick={handleCertify}
              >
                <Award className="h-4 w-4 mr-2" />
                Certify Report
              </Button>

              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-3">
                  {canVerify && 'This report is pending verification.'}
                  {canValuate && 'This report has been verified and is ready for valuation.'}
                  {canCertify && 'This report has been valuated and is ready for certification.'}
                  {report.status === 'certified' && 'This report has been certified.'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Additional Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start">
                <Share2 className="h-4 w-4 mr-2" />
                Share Report
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReportDetail;
